import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { paymentsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const PaymentSuccess = () => {
  usePageTitle('Payment');
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [details, setDetails] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await paymentsAPI.getStatus(sessionId);
      setDetails(res.data);

      if (res.data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-ambient">
          {status === 'loading' || status === 'processing' ? (
            <>
              <Loader2 className="w-16 h-16 text-mint-fresh mx-auto mb-6 animate-spin" />
              <h1 className="font-heading font-bold text-2xl text-dark-slate mb-2">
                Processing Payment
              </h1>
              <p className="text-slate-gray">Please wait while we confirm your payment...</p>
            </>
          ) : status === 'success' ? (
            <>
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
              <h1 className="font-heading font-bold text-2xl text-dark-slate mb-2">
                Payment Successful!
              </h1>
              <p className="text-slate-gray mb-6">
                Thank you for your payment. Your transaction has been completed successfully.
              </p>
              {details && (
                <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                  <p className="text-sm text-slate-gray">
                    Amount: <span className="font-semibold text-dark-slate">${(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()}</span>
                  </p>
                </div>
              )}
              <Link to="/dashboard">
                <Button className="w-full rounded-full bg-dark-slate" data-testid="return-dashboard">
                  Return to Dashboard
                </Button>
              </Link>
            </>
          ) : status === 'expired' ? (
            <>
              <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-6" />
              <h1 className="font-heading font-bold text-2xl text-dark-slate mb-2">
                Session Expired
              </h1>
              <p className="text-slate-gray mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Link to="/dashboard">
                <Button className="w-full rounded-full bg-dark-slate">
                  Return to Dashboard
                </Button>
              </Link>
            </>
          ) : (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
              <h1 className="font-heading font-bold text-2xl text-dark-slate mb-2">
                Payment Issue
              </h1>
              <p className="text-slate-gray mb-6">
                There was an issue processing your payment. Please contact support if you were charged.
              </p>
              <Link to="/dashboard">
                <Button className="w-full rounded-full bg-dark-slate">
                  Return to Dashboard
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PaymentSuccess;
